/**
 * STRENGTH SESSION FORM - Quick entry modal for logging lifts
 */

import React, { useState } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface StrengthSessionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const StrengthSessionForm: React.FC<StrengthSessionFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    exercise: 'bench_press',
    exercise_custom: '',
    weight_kg: '',
    reps: '',
    sets: '3',
    rpe: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        ...formData,
        weight_kg: parseFloat(formData.weight_kg),
        reps: parseInt(formData.reps, 10),
        sets: parseInt(formData.sets, 10),
        rpe: formData.rpe ? parseInt(formData.rpe, 10) : null,
        exercise_custom: formData.exercise === 'other' ? formData.exercise_custom : null
      };

      await apiClient.post('/api/strength-sessions/', payload);
      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      console.error('Error creating strength session:', err);
      setError(err.response?.data?.error || 'Failed to log lift');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      exercise: 'bench_press',
      exercise_custom: '',
      weight_kg: '',
      reps: '',
      sets: '3',
      rpe: '',
      notes: ''
    });
  };

  const calculateE1RM = (): string | null => {
    const weight = parseFloat(formData.weight_kg);
    const reps = parseInt(formData.reps, 10);

    if (!weight || !reps || reps < 1) return null;

    // Epley formula: weight * (1 + reps/30)
    const e1rm = weight * (1 + reps / 30);
    return e1rm.toFixed(1);
  };

  const e1rm = calculateE1RM();

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: COLORS.bg.panel,
        border: `1px solid ${COLORS.border.default}`,
        borderRadius: RADIUS,
        padding: SPACING[32],
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: SPACING[24]
        }}>
          <h2 style={{
            fontSize: TYPOGRAPHY.size.xl,
            fontWeight: 600,
            color: COLORS.text.primary,
            margin: 0
          }}>
            Log Lift
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: COLORS.text.secondary,
              padding: SPACING[8]
            }}
          >
            <XMarkIcon style={{ width: 24, height: 24 }} />
          </button>
        </div>

        {error && (
          <div style={{
            padding: SPACING[16],
            background: 'rgba(239, 68, 68, 0.1)',
            border: `1px solid rgb(239, 68, 68)`,
            borderRadius: RADIUS,
            color: COLORS.status.error,
            marginBottom: SPACING[24],
            fontSize: TYPOGRAPHY.size.sm
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
          </div>

          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Exercise
            </label>
            <select
              value={formData.exercise}
              onChange={(e) => setFormData({ ...formData, exercise: e.target.value })}
              required
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            >
              <option value="bench_press">Bench Press</option>
              <option value="squat">Squat</option>
              <option value="deadlift">Deadlift</option>
              <option value="overhead_press">Overhead Press</option>
              <option value="barbell_row">Barbell Row</option>
              <option value="pull_up">Pull Up</option>
              <option value="dumbbell_press">Dumbbell Press</option>
              <option value="leg_press">Leg Press</option>
              <option value="romanian_deadlift">Romanian Deadlift</option>
              <option value="lat_pulldown">Lat Pulldown</option>
              <option value="other">Other</option>
            </select>
          </div>

          {formData.exercise === 'other' && (
            <div style={{ marginBottom: SPACING[24] }}>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.size.sm,
                fontWeight: 500,
                color: COLORS.text.secondary,
                marginBottom: SPACING[8]
              }}>
                Exercise Name
              </label>
              <input
                type="text"
                value={formData.exercise_custom}
                onChange={(e) => setFormData({ ...formData, exercise_custom: e.target.value })}
                required
                placeholder="e.g. Bulgarian Split Squat"
                style={{
                  width: '100%',
                  padding: SPACING[16],
                  background: COLORS.bg.base,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  color: COLORS.text.primary,
                  fontSize: TYPOGRAPHY.size.base
                }}
              />
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: SPACING[16],
            marginBottom: SPACING[24]
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.size.sm,
                fontWeight: 500,
                color: COLORS.text.secondary,
                marginBottom: SPACING[8]
              }}>
                Weight (kg)
              </label>
              <input
                type="number"
                step="0.5"
                value={formData.weight_kg}
                onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                required
                min="0"
                placeholder="100"
                style={{
                  width: '100%',
                  padding: SPACING[16],
                  background: COLORS.bg.base,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  color: COLORS.text.primary,
                  fontSize: TYPOGRAPHY.size.base
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.size.sm,
                fontWeight: 500,
                color: COLORS.text.secondary,
                marginBottom: SPACING[8]
              }}>
                Reps
              </label>
              <input
                type="number"
                value={formData.reps}
                onChange={(e) => setFormData({ ...formData, reps: e.target.value })}
                required
                min="1"
                placeholder="5"
                style={{
                  width: '100%',
                  padding: SPACING[16],
                  background: COLORS.bg.base,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  color: COLORS.text.primary,
                  fontSize: TYPOGRAPHY.size.base
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.size.sm,
                fontWeight: 500,
                color: COLORS.text.secondary,
                marginBottom: SPACING[8]
              }}>
                Sets
              </label>
              <input
                type="number"
                value={formData.sets}
                onChange={(e) => setFormData({ ...formData, sets: e.target.value })}
                required
                min="1"
                placeholder="3"
                style={{
                  width: '100%',
                  padding: SPACING[16],
                  background: COLORS.bg.base,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  color: COLORS.text.primary,
                  fontSize: TYPOGRAPHY.size.base
                }}
              />
            </div>
          </div>

          {e1rm && (
            <div style={{
              padding: SPACING[16],
              background: 'rgba(139, 92, 246, 0.1)',
              border: `1px solid rgb(139, 92, 246)`,
              borderRadius: RADIUS,
              marginBottom: SPACING[24]
            }}>
              <div style={{
                fontSize: TYPOGRAPHY.size.xs,
                color: COLORS.text.secondary,
                marginBottom: SPACING[8]
              }}>
                Estimated 1-Rep Max
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.size.xl,
                fontWeight: 600,
                color: COLORS.status.info
              }}>
                {e1rm} kg
              </div>
            </div>
          )}

          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              RPE (1-10, optional)
            </label>
            <input
              type="number"
              value={formData.rpe}
              onChange={(e) => setFormData({ ...formData, rpe: e.target.value })}
              min="1"
              max="10"
              placeholder="8"
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
          </div>

          <div style={{ marginBottom: SPACING[32] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Notes (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Any additional notes about this lift..."
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base,
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: SPACING[16],
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: `${SPACING[12]} ${SPACING[24]}`,
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.secondary,
                cursor: 'pointer',
                fontSize: TYPOGRAPHY.size.base,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: `${SPACING[12]} ${SPACING[24]}`,
                background: COLORS.accent.default,
                border: 'none',
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: TYPOGRAPHY.size.base,
                fontWeight: 500,
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Log Lift'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StrengthSessionForm;
